<template>
  <div @click="selectedInputItem = null">
    <breadcrumbs home="Bundle"/>

    <v-card flat>
      <v-form v-model="valid" ref="form">
        <v-container class="pt-6 px-6 purtrak-scrollable" fluid>
          <v-row dense>
            <v-col cols="12" sm="6" md="5">
              <v-text-field label="Name" v-model="bundle.name" :rules="[fieldRules.required,fieldRules.maxLength(100)]" :counter="100" outlined dense/>
            </v-col>
            <v-col cols="2" lg="1">
              <v-checkbox label="Active" v-model="bundle.active" dense class="mt-1"/>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="6" lg="4">
              <v-tooltip open-on-hover bottom nudge-top="25" max-width="300">
                <template v-slot:activator="{on}">
                  <v-checkbox v-model="bundle.displayComponents" dense>
                    <span slot="label" v-on="on">Display bundle items when shipped</span>
                  </v-checkbox>
                </template>
                <div>If selected, then each bundle item appears separately with the ability to
                set individual item prices and taxes.
                </div>
                <div class="mt-2">When not selected, the bundle appears as a single item with
                  a price and tax for the entire bundle.
                </div>
              </v-tooltip>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="6" md="5">
              <v-text-field label="Description on sales forms" v-model="bundle.sellingDescription" :rules="[fieldRules.maxLength(100)]" :counter="100" outlined dense :disabled="bundle.displayComponents"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6" sm="3" md="2">
              <v-text-field label="SKU" v-model="bundle.sku" :rules="[fieldRules.maxLength(20)]" :counter="20" outlined dense :disabled="bundle.displayComponents"/>
            </v-col>
            <v-col cols="6" sm="3" md="2">
              <v-text-field label="UPC" v-model="bundle.upc" :counter="12" :rules="[upc]" outlined dense :disabled="bundle.displayComponents"/>
            </v-col>
            <v-col cols="6" sm="3" md="2">
              <decimal-number-input label="Unit weight" v-model="bundle.weight" prefix="" suffix="kg" :precision="3" :disabled="bundle.displayComponents"
                                    hint="For shipping documents"/>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12" sm="6" md="4" v-if="connectedToAccounting &&  values">
              <account-picker v-model="bundle.incomeAccountRef" label="Income account" :disabled="bundle.displayComponents"
                              :accounts="incomeAccounts" :addTypes="['Income']"
                              hint="The income account is used to track sales of this bundle."/>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="10" lg="8">
              <outlined-block title="Items">
                <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                              :items="bundle.items" item-key="key" no-data-text="Items are required"
                              :headers="headers" class="no-sort"
                              :item-class="item => item.key=== selectedInputItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                              @click.native.stop=""
                              @click:row="item => {selectedInputItem = item.key; $nextTick(() => this.$refs.form.validate())}"
                              ref="tableBody">
                  <template v-slot:item.add="{index}">
                    <v-icon class="handle mt-n2">mdi-drag</v-icon>
                    <v-btn class="mt-n2" icon @click.stop="deleteItem(index)" x-small><v-icon>mdi-delete</v-icon></v-btn>
                  </template>
                  <template v-slot:item.type="{item}">
                    <template v-if="selectedInputItem === item.key">
                      <multi-level-select outlined label="Item" :menuData="inputTypes" :first.sync="item.type" :second.sync="item.id" @change="setPrice(item)"/>
                      <v-checkbox v-if="item.type==='Product' && !settings.lotTrackingForProducts"
                                  v-model="item.inBondProduct" dense hide-details class="mt-n4">
                        <template v-slot:label><span class="text-caption">Take product from in-bond inventory</span></template>
                      </v-checkbox>
                    </template>
                    <div v-else class="ml-1" :class="{'error--text':!item.type}">{{itemDescription(item)}}</div>
                  </template>
                  <template v-slot:item.quantity="{item}">
                    <decimal-number-input v-if="selectedInputItem === item.key" label="Quantity" v-model="item.quantity" prefix="" @change="calculatePrice()"
                          :suffix="trackUnits(item)" :precision="0" class="text-body-2"/>
                    <div v-else class="mr-1" :class="{'error--text':item.quantity<0}">{{item.quantity | number(0)}} {{trackUnits(item)}}</div>
                  </template>
                  <template v-slot:item.price="{item}">
                    {{item.price | currency}}
                  </template>
                  <template v-slot:footer="{widths}">
                    <v-divider/>
                    <div @click="selectedInputItem = null" style="width:100%" class="py-2 px-0">
                      <div :style="'width:'+widths[0]+'px'" class="text-center">
                        <v-btn fab color="primary" x-small @click.stop="addItem"><v-icon small>mdi-plus</v-icon></v-btn>
                      </div>
                    </div>
                  </template>
                </v-data-table>
              </outlined-block>
            </v-col>
          </v-row>
          <v-row v-if="!bundle.displayComponents">
            <v-col cols="6" sm="3" md="2">
              <decimal-number-input v-model="bundle.price" label="Price" :disabled="bundle.calculatePrice"/>
            </v-col>
            <v-col cols="6" sm="4" md="3">
              <v-checkbox label="Price based on item prices" dense v-model="bundle.calculatePrice"/>
            </v-col>
            <v-col cols="6" sm="3" md="2">
              <v-autocomplete outlined dense label="Default tax" v-model="bundle.taxId" :items="taxes" item-value="id" item-text="name"/>
            </v-col>
            <v-col cols="12" sm="4" md="3">
              <v-checkbox dense label="Commission exempt" v-model="bundle.commissionExempt"/>
            </v-col>
          </v-row>
          <v-row v-if="!bundle.displayComponents">
            <v-col cols="6" sm="3" md="2" v-if="!settings.lotTrackingForProducts">
              <decimal-number-input v-model="bundle.inBondPrice" label="In-bond price" :disabled="bundle.calculatePrice"/>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
      <v-divider/>
      <v-card-actions class="px-6">
        <v-spacer/>
        <v-btn plain color="primary" @click="cancel">Cancel</v-btn>
        <v-btn color="primary" :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>
    <confirm-leave v-model="next"/>
  </div>
</template>

<style>
  .pt-table-top {vertical-align: top;padding-bottom:10px !important}
</style>

<script>
import confirmLeave from '@/mixins/confirmLeave'
import barcode from '@/mixins/barcode'
import sortableJS from 'sortablejs';
import createHierarchyList from '@/mixins/createHierarchyList'
import DecimalNumberInput from '../../../components/DecimalNumberInput.vue';

export default {
  components: { DecimalNumberInput },
  name: 'InventoryBundlesEdit',
  mixins: [confirmLeave,barcode,createHierarchyList],
  props: {
    bundleId: {type:String, required:false}
  },
  data() {
    return {
      accounts: [],
      companyAccounts: {},
      taxes: [],
      bundle: {items:[]},
      dirty: false,
      saving: false,
      loading: false,
      valid: true,
      items: [],
      itemCategories: [],
      productCategories: [],
      products: [],
      price: 0,
      selectedInputItem: null,
      sortable: null
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    settings() {
      return this.company.settings;
    },
    connectedToAccounting() {
      return this.company.connectedToAccounting;
    },
    incomeAccounts() {
      return this.accounts.filter(account => account.type === 'Income');
    },
    values() {
      return this.$store.getters.company.permissions.includes('values') && this.settings.useAccounts;
    },
    inputTypes() {
      const types = [];
      if (this.products.length) {
        if (this.productCategories.length) {
          const categories = this.createHierarchyList(this.productCategories);
          for (const category of categories) {
            const sub = this.products.filter(product => product.category?.id === category.id);
            if (sub.length)
              category.menu = sub;
            else
              category.disabled = true;
          }
//          types.push({name:'Product',id:'Product',menu:[...categories.filter(category => !category.disabled)]});
          types.push({name:'Product',id:'Product',menu:categories});
          const leftOver = this.products.filter(product => !product.category?.id);
          if (leftOver.length)
            types[0].menu.push({divider:true},{name:'Other products',id:'x',menu:leftOver})

        }
        else
          types.push({name:'Product',id:'Product',menu:this.products});
      }
      if (this.items.length) {
        if (this.itemCategories.length) {
          const categories = this.createHierarchyList(this.itemCategories);
          for (const category of categories) {
            const sub = this.items.filter(item => item.category?.id === category.id);
            if (sub.length)
              category.menu = sub;
            else
              category.disabled = true;
          }
//          types.push({name:'Item',id:'Item',menu:[...categories.filter(category => !category.disabled)]});
          types.push({name:'Item',id:'Item',menu:categories});
          const leftOver = this.items.filter(item => !item.category?.id);
          if (leftOver.length)
            types[1].menu.push({divider:true},{name:'Other items',id:'x',menu:leftOver})
        }
        else
          types.push({name:'Item',id:'Item',menu:this.items});
      }
      return types;
    },
    headers() {
      const headers =  [{text:'',value:'add',width:'10%',align:'center',cellClass:'pa-0',divider:true},
                        {text:'Item',value:'type',width:'50%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt-table-top'},
                        {text:'Quantity',value:'quantity',width:'20%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt-table-top',align:'right'}];
      if (!this.bundle.displayComponents)
        headers.push({text:'Unit price',value:'price',width:'20%',divider:true,class:'px-2',cellClass:'py-2 px-2 pt-table-top',align:'right'});
      return headers;
    }
  },
  watch: {
    bundle: {
      handler() {this.dirty = true},
      immediate: true,
      deep: true
    }
  },
  async created() {
    this.loading = true;
    this.$lazyListLoad(this.itemCategories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Item'}]);
    this.$lazyListLoad(this.productCategories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Product'}]);
    if (!this.settings.lotTrackingForDutyPaid)
      this.$lazyListLoad(this.products, 'products2', 'id,name,defaultPrice,category{id},bottlesPerUnit', 100, [{field:'active', comparator:'eq', value:'true'}])
    this.$lazyListLoad(this.items, 'items2', 'id,name,trackUnits,defaultPrice,lotTracking,category{id}', 100, [{field:'active', comparator:'eq', value:'true'},{field:'lotTracking',comparator:'eq',value:'false'},{field:'sold',comparator:'eq',value:'true'}]);
    this.$lazyListLoad(this.taxes, 'taxes2', 'id,name', 100, [{field:'salesRate', comparator:'ne', value:''}]);
    const results = await this.$apiCall(`query InventoryBundlesEdit($accounting:Boolean!, $currency:String!, $edit:Boolean!,$id:ID!) {
                                            bundle(id:$id) @include(if:$edit) {
                                              id,name,displayComponents,sku,active,
                                              price,calculatePrice,
                                              inBondPrice,
                                              items {type,id,quantity,price,inBondProduct},
                                              weight,upc,commissionExempt,
                                              sellingDescription,tax{id},incomeAccountRef,
                                              pictureURL
                                            }
                                            accounts(currency:$currency) @include(if:$accounting) {
                                              ref,name,type
                                            }
                                            companyAccounts @skip(if:$edit) {
                                              bundleIncome
                                            }
                                          }`,
                                        {accounting:this.connectedToAccounting,
                                         currency:this.settings.homeCurrency,
                                         edit:this.bundleId!==undefined,id:this.bundleId?this.bundleId:''});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }

    if (this.connectedToAccounting)
      this.accounts = results.data.accounts;

    if (this.bundleId) {
      this.bundle = {...results.data.bundle, taxId:results.data.bundle.tax?.id, tax:undefined};
      let key = Date.now();
      this.bundle.items.forEach(item => item.key = key++);
    }
    else
      this.bundle = {name:'',displayComponents:false,sku:'',active:true,items:[], incomeAccountRef:results.data.companyAccounts.bundleIncome,
                    weight:0,upc:'',sellingDescription:'',taxId:null, calculatePrice:true, price:0,inBondPrice:0,commissionExempt:false};
    this.$nextTick(() => this.dirty = false);
    this.loading = false;
  },
  mounted() {
    const _self = this;
    this.sortable = sortableJS.create(this.$refs.tableBody.$el.querySelector('tbody'), {
      handle: '.handle',
      onEnd({newIndex, oldIndex}) {
        const rowSelected = _self.bundle.items.splice(oldIndex, 1)[0];
        _self.bundle.items.splice(newIndex, 0, rowSelected);
      }
    })
  },
  beforeDestroy() {
    this.sortable.destroy();
  },
  methods: {
    itemDescription(item) {
      if (!item.type)
        return 'MISSING';
      const lists = {'Product':this.products,'Item':this.items};
      return `${item.type}: ${lists[item.type]?.find(i => i.id === item.id)?.name} ${item.type === 'Product' && item.inBondProduct ? '(in-bond)' : ''}`;
    },
    calculatePrice() {
//      if (this.bundle.calculatePrice)
        this.bundle.price = this.bundle.items.reduce((total,item) => total + item.quantity * item.price, 0);
    },
    validate() {
      this.$nextTick(() => this.$refs.form.validate());
    },
    addItem() {
      const item = {type:'',id:null,quantity:1,price:0,inBondProduct:false,key:Date.now()};
      this.bundle.items.push(item);
      this.selectedInputItem = item.key;
      this.validate();
      this.calculatePrice();
    },
    deleteItem(index) {
      this.bundle.items.splice(index, 1);
      this.calculatePrice();
    },
    setPrice(item) {
      item.price = item.type === 'Product' ? this.products.find(p => p.id === item.id).defaultPrice :
                                             this.items.find(i => i.id === item.id).defaultPrice;
      this.calculatePrice();
    },
    trackUnits(item) {
      if (item.type === 'Product')
        return (this.products.find(p => p.id === item.id)?.bottlesPerUnit ?? 1) > 1 ? 'units' : 'bottles';
      return this.items.find(i => i.id === item.id)?.trackUnits;
    },
    cancel() {
      this.$router.back();
    },
    async save() {
      const incomplete = this.bundle.items.some(item => !item.id ||
                                                (item.type === 'Spirit' || item.type === 'Wine' || item.type === 'NonExcise') && !item.vesselId && this.blend.completed ||
                                                item.type === 'Item' && item.lotTracking ||
                                                item.quantity < 0);
      if (!this.valid || incomplete) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }
      this.saving = true;
      // eslint-disable-next-line no-unused-vars
      const {pictureURL,items,...input} = this.bundle;
      input.items = this.bundle.items.map(item => {
        const {key, ...itemCore} = item;
        return itemCore;
      });
      const results = await this.$apiCall(`mutation InventoryBundlesEdit($input: BundleInput!) {
                                             bundleCreate(input:$input) {id}
                                           }`,
                                          {input})
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`${this.bundle.id?'Updated':'Created'} bundle ${this.bundle.name}`});
      }
    }
  }
}
</script>